import React, { useCallback, useEffect, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    type Event,
    type EventCreated,
    EventKind,
    type PageInfo,
    ProximityZone,
    ShockEventLevel,
} from "../../api";
import { store } from "../../store";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeftRight, VolumeX } from "lucide-react";

type VehicleNameProps = {
    manufacturer?: string | null;
    model?: string | null;
    serialNumber: string;
};

const VehicleName = ({ serialNumber, model, manufacturer }: VehicleNameProps): JSX.Element => {
    const { t } = useTranslation();
    const name = ((manufacturer ?? "") + " " + (model ?? "")).trim();

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span>{serialNumber}</span>
                </TooltipTrigger>
                <TooltipContent>
                    {name || t("component.vehicleDetails.unspecifiedModel")}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export type ListEventsProps = React.PropsWithChildren & {
    selectedEventId?: string;
    onSelectEvent: (event?: Event) => void;
    hidePagination?: boolean;
    events: Event[];
    eventsMetaData?: PageInfo;
    refreshEvents: () => void;
    page: number;
    setPage: (page: number) => void;
};

export const ListEvents = ({
    onSelectEvent,
    hidePagination = false,
    selectedEventId,
    events,
    refreshEvents,
    eventsMetaData,
    page,
    setPage,
}: ListEventsProps): JSX.Element => {
    const { t } = useTranslation();
    const viewportRef = useRef<HTMLDivElement>(null);

    /** Display a toast for an event. Conditionally render an info, a warning or
        an error toast depending on the severity of the event. */
    const toastEvent = useCallback(
        async (data: EventCreated) => {
            if (data.kind === EventKind.BADGE_ZONE_VIOLATION) {
                const badgeZoneViolation =
                    await store.badgeZoneViolationEventsApi.badgeZoneViolationEventsReadBadgeZoneViolationIncident(
                        { id: data.eventId },
                    );
                const toastTitle = t(
                    `listEvents.toast.badgeZoneViolation.${badgeZoneViolation.triggeredZone}`,
                );
                if (badgeZoneViolation.triggeredZone === ProximityZone.ZONE1)
                    toast.error(toastTitle);
                else if (badgeZoneViolation.triggeredZone === ProximityZone.ZONE2)
                    toast.warning(toastTitle);
            } else if (data.kind === EventKind.VEHICLE_ZONE_VIOLATION) {
                const vehicleZoneViolation =
                    await store.vehicleZoneViolationEventsApi.vehicleZoneViolationEventsReadVehicleZoneViolationIncident(
                        { id: data.eventId },
                    );
                const toastTitle = t(
                    `listEvents.toast.vehicleZoneViolation.${vehicleZoneViolation.triggeredZone}`,
                );
                if (vehicleZoneViolation.triggeredZone === ProximityZone.ZONE1)
                    toast.error(toastTitle);
                else if (vehicleZoneViolation.triggeredZone === ProximityZone.ZONE2)
                    toast.warning(toastTitle);
            } else if (data.kind === EventKind.SHOCK) {
                const shockEvent = await store.shockEventsApi.shockEventsReadShockEvent({
                    shockEventId: data.eventId,
                });
                const toastTitle = t(`listEvents.toast.shock.${shockEvent.shockLevel}`);
                if (shockEvent.shockLevel === ShockEventLevel.LIGHT) toast.info(toastTitle);
                else if (shockEvent.shockLevel === ShockEventLevel.MEDIUM)
                    toast.warning(toastTitle);
                else if (shockEvent.shockLevel === ShockEventLevel.SEVERE) toast.error(toastTitle);
            }
        },
        [t],
    );

    /** Toast notifications whenever the backend notifies us about new events. */
    useEffect(() => {
        async function handleEventCreated(message: MessageEvent): Promise<void> {
            refreshEvents();
            await toastEvent(JSON.parse(message.data) as EventCreated);
        }

        store.serviceSse.eventSource?.addEventListener("event-created", handleEventCreated);

        return () => {
            store.serviceSse.eventSource?.removeEventListener("event-created", handleEventCreated);
            store.serviceSse.eventSource?.close();
        };
    }, [refreshEvents, toastEvent]);

    useEffect(() => {
        if (viewportRef.current) {
            viewportRef.current.scrollTop = 0;
        }
    }, [page]);

    return (
        <div className="relative flex h-full flex-col overflow-hidden">
            <ScrollArea viewportRef={viewportRef} className="flex flex-col">
                <Table className="tabular-nums">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t("listEvents.table.column.eventType")}</TableHead>
                            <TableHead>{t("listEvents.table.column.vehicles")}</TableHead>
                            <TableHead className="text-right">
                                {t("listEvents.table.column.eventTime")}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    {events && (
                        <TableBody>
                            {events.map((event) => {
                                const aName = (
                                    <VehicleName
                                        serialNumber={event.aVehicleSerialNumber}
                                        manufacturer={event.aVehicleManufacturer}
                                        model={event.aVehicleModel}
                                    />
                                );
                                const bName = event.bVehicleSerialNumber ? (
                                    <VehicleName
                                        serialNumber={event.bVehicleSerialNumber}
                                        manufacturer={event.bVehicleManufacturer}
                                        model={event.bVehicleModel}
                                    />
                                ) : undefined;
                                // Order the vehicle data based on the serial number.
                                const names =
                                    !event.bVehicleSerialNumber ||
                                    event.aVehicleSerialNumber <= event.bVehicleSerialNumber
                                        ? [aName, bName]
                                        : [bName, aName];

                                return (
                                    <TableRow
                                        key={event.id}
                                        className={cn(
                                            "text-nowrap",
                                            event.id === selectedEventId && "bg-muted",
                                        )}
                                        onClick={() => onSelectEvent(event)}
                                    >
                                        <TableCell className="font-medium" padded>
                                            <div className="flex items-center gap-2.5">
                                                <Badge>
                                                    {t(`schema.event.kind.${event.kind}`)}
                                                </Badge>
                                                {event.driverMutingEnabled && (
                                                    <Badge variant="outline">
                                                        <VolumeX size={16} strokeWidth={3} />
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2.5">
                                                {names[0]}
                                                {event.bVehicleSerialNumber && (
                                                    <>
                                                        <ArrowLeftRight size={16} />
                                                        {names[1]}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {store.i18n.formatDateTime(event.time)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    )}
                </Table>
            </ScrollArea>
            {eventsMetaData && !hidePagination && (
                <div className="mt-auto pt-4">
                    <Pagination className="justify-end">
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    className={cn(
                                        "cursor-pointer",
                                        page <= 0 ? "pointer-events-none opacity-50" : undefined,
                                    )}
                                    label={t("component.pagination.previous")}
                                    onClick={() => setPage(page - 1)}
                                />
                            </PaginationItem>
                            <PaginationItem className="font-medium">{`${page + 1} / ${eventsMetaData.totalPages}`}</PaginationItem>
                            <PaginationItem>
                                <PaginationNext
                                    className={cn(
                                        "cursor-pointer",
                                        page >= eventsMetaData.totalPages - 1
                                            ? "pointer-events-none opacity-50"
                                            : undefined,
                                    )}
                                    label={t("component.pagination.next")}
                                    onClick={() => setPage(page + 1)}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};
